<template lang="pug">
.pay-subscription
  v-container.white.px-6(style="max-width:600px")
    v-layout.my-4.align-center
      v-img(:src="require('@/assets/images/logo_main.png')" max-height="40" max-width="100" contain)

    //- Step 1: Auth
    template(v-if="!authenticated")
      v-card.mt-12.pa-6(outlined)
        h5.text-center.mb-6 {{ $t('subscription_payment') }}

        template(v-if="!authLinkSent")
          p.mb-1.label.basic600--text.text-uppercase {{ $t('email_address') }}
          v-text-field.p1.pt-2(v-model="email" outlined dense solo flat background-color="basic200" height="40" disabled)
          v-btn.mt-2.button-AA-medium.white--text(color="info500" block depressed @click="sendAuthLink" :loading="sendingAuthLink" :disabled="!email" height="40") {{ $t('send_login_link') }}
          .p2.text-center.primary400--text.mt-3 {{ $t('login_link_desc') }}

        template(v-else)
          v-alert.mb-4(color="info500" dark dense)
            .s2 {{ $t('check_email_inbox') }}
            .c1 {{ $t('check_email_inbox_desc') }}

          p.mb-1.label.basic600--text.text-uppercase {{ $t('email_address') }}
          v-text-field.p1.pt-2(v-model="email" outlined dense solo flat background-color="basic200" height="40" disabled)
            eva-icon(slot="append" name="checkmark-circle-2-outline" width="20" height="20" :fill="$vuetify.theme.themes[$theme].success400")

          v-layout.my-4(v-if="authWaiting")
            div.mx-auto
              v-layout
                v-progress-circular.my-auto(indeterminate :size="16" :width="2" color="info500")
                .p2.my-auto.ml-3 {{ $t('waiting_for_approval') }}...

          div(v-else-if="authExpired")
            v-layout.my-4
              div.mx-auto
                v-layout
                  v-icon(color="error" small) mdi-close-circle-outline
                  .p2.ml-3.my-auto {{ $t('link_expired') }}&nbsp;
                    a.info500--text.cursor-pointer(@click="sendAuthLink") {{ $t('click_to_resend') }}

    //- Step 2: Subscription content (after auth)
    template(v-if="authenticated")
      v-layout.my-6(v-if="loading")
        v-row.fill-height.ma-0(align='center' justify='center')
          v-progress-circular.ma-auto(:width="2" :size="50" color="primary" indeterminate)

      .content-layout(v-else)
        .s1.mb-1(v-if="companyData") {{ companyData.name }}
        h5.font-weight-bold {{ $t('subscription') }}

        //- Active Subscriptions
        div.my-4
          .s1.mb-2 {{ $t('active_subscriptions') }}

          div(v-if="activeSubscriptions.length === 0")
            v-card.pa-6.text-center(outlined flat)
              .p2.basic600--text {{ $t('no_active_subscription') }}

          v-card.mb-3(v-for="sub in activeSubscriptions" :key="sub.name" outlined flat)
            .pa-4
              .p2 {{ sub.name }}
              div.d-flex.mt-1(v-if="sub.isTrial")
                .s2.text-uppercase {{ $t('trial') }}
              div.d-flex.mt-1(v-else)
                .s2.text-uppercase {{ sub.plan }}
              .c1.primary400--text.mt-1(v-if="sub.isTrial && sub.endDate")
                span(v-if="sub.expired") {{ $t('trial_ended_at') }} {{ sub.endDate | humanizeDate }}
                span(v-else) {{ $t('trial_ends') }}: {{ sub.endDate | humanizeDate }}
              .c1.mt-1(v-if="!sub.isTrial && sub.endDate") {{ $t('next_billing_date') }}: {{ sub.endDate | humanizeDate }}
              .c1.primary400--text.mt-1(v-if="!sub.isTrial && sub.contractEndDate") {{ $t('contract_ends') }}: {{ sub.contractEndDate | humanizeDate }}

        v-divider.my-4

        //- Total Due
        div.my-4
          .s1.mb-2 {{ $t('total_due') }}
          v-card(outlined flat)
            .pa-4
              v-layout.align-center(v-if="loadingBills")
                v-progress-circular(:width="2" :size="20" color="primary" indeterminate)
              template(v-else)
                v-layout.align-center(v-if="totalDue > 0")
                  .h5.font-weight-bold {{ totalDue | displayPrice(currency) }}
                .p2.basic600--text(v-else) {{ $t('no_pending_bills') }}

        v-divider.my-4

        //- Payment Method
        div.my-4(v-if="totalDue > 0")
          .s1.mb-2 {{ $t('payment_method') }}
          v-select.mt-1(v-model="paymentMethod" :items="paymentMethods" item-text="name" item-value="id" background-color="basic200" height="40" outlined dense hide-details append-icon="mdi-chevron-down")

          //- Card
          div.mt-3(v-if="paymentMethod === 'card'")
            card-list(@change="onCardChange")

          //- Online banking (FPX) — bank picker only for MYR
          div.mt-3(v-else-if="paymentMethod === 'fpx' && companyData && companyData.currency.currency_code === 'MYR'")
            .s2.mb-2 {{ $t('choose_bank') }}
            v-card(outlined flat)
              FPXPicker(v-model="fpxBankCode")

        v-divider.my-4(v-if="totalDue > 0")

        //- Pay Button
        div.my-4(v-if="totalDue > 0")
          v-btn(block color="info500" dark depressed :loading="isProcessing" :disabled="!canPay" @click="payAllBills")
            .button-AA-medium {{ $t('pay_now') }}
          .c1.basic600--text.text-center.mt-2(v-if="paymentMethod === 'card' && !selectedCard") {{ $t('select_card_to_pay') }}

  //- Processing dialog
  y-dialog(v-model='isProcessing' persistent width='300')
    v-card(color='primary' dark)
      v-card-text.pt-3
        | {{ $t('processing_payment') }}
        v-progress-linear.mb-0.mt-4(indeterminate color='white')
        div.mt-4.font-weight-bold {{ $t('do_not_refresh') }}

</template>

<script>
import { mapActions } from 'vuex'
import CardList from '@/components/billing/CardList.vue'
import FPXPicker from '@/components/FPXPicker.vue'

export default {
  name: 'PaySubscription',
  components: {
    CardList,
    FPXPicker
  },
  data: () => ({
    // Auth
    email: '',
    slug: '',
    authenticated: false,
    sendingAuthLink: false,
    authLinkSent: false,
    authWaiting: false,
    authExpired: false,
    authToken: null,
    manualCheckInterval: null,
    authLinkRecheckCount: 0,

    // Subscription data
    loading: false,
    companyData: null,
    subscriptions: [],
    selectedCard: null,
    paymentMethod: 'card',
    fpxBankCode: null,
    loadingBills: false,
    totalDue: 0,
    unpaidBills: [],
    isProcessing: false
  }),
  computed: {
    currency () {
      // displayPrice() needs a normalized object ({ code, symbol, multiplier, exponent }),
      // not the raw API currency ({ currency_code, currency_symbol, currency_sub_unit })
      const c = this.companyData && this.companyData.currency
      if (c) {
        return {
          code: c.currency_code,
          symbol: c.currency_symbol,
          multiplier: c.currency_sub_unit,
          exponent: c.currency_sub_unit.toString().length - 1
        }
      }
      return { code: 'MYR', symbol: 'RM', multiplier: 100, exponent: 2 }
    },
    paymentMethods () {
      return [
        { name: this.$t('credit_debit_card'), id: 'card' },
        { name: this.$t('online_banking'), id: 'fpx' }
      ]
    },
    canPay () {
      if (this.paymentMethod === 'card') return !!this.selectedCard
      if (this.paymentMethod === 'fpx') {
        // For MYR a bank must be picked; other currencies select the bank on the gateway page
        if (this.companyData && this.companyData.currency.currency_code === 'MYR') return !!this.fpxBankCode
        return true
      }
      return false
    },
    activeSubscriptions () {
      const subs = []
      const planGroupMap = {
        'YZ_CMS': this.$t('clinic'),
        'YCLINIC': this.$t('appointment'),
        'REVIEW': this.$t('review'),
        'PURCHASE_HUB': 'Purchase Hub',
        'MARKETING': 'Marketing',
      }
      for (const s of this.subscriptions) {
        const name = planGroupMap[s.plan_group]
        if (!name) continue
        const isTrial = s.contract.code.includes('_TRIAL')
        if (isTrial && !s.expired) {
          subs.push({
            name,
            plan: this.$t('trial'),
            isTrial: true,
            expired: false,
            endDate: s.end_date,
            contractEndDate: null
          })
        } else if (!isTrial && !s.expired) {
          const code = s.contract.code
          const isYearly = code.includes('_YY')
          subs.push({
            name,
            plan: this.getContractDisplayName(code),
            isTrial: false,
            expired: false,
            endDate: s.end_date,
            contractEndDate: (isYearly || code.includes('_YM')) ? s.contract_end_date : null
          })
        }
      }
      return subs
    },
  },
  created () {
    this.slug = this.$route.query.slug || ''
    this.email = this.$route.query.email || ''
  },
  beforeDestroy () {
    if (this.manualCheckInterval) {
      clearInterval(this.manualCheckInterval)
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    // The API may return either a paginated object ({ results: [...] }) or a bare array
    extractList (data) {
      if (Array.isArray(data)) return data
      return (data && data.results) || []
    },
    getContractDisplayName (code) {
      if (code.includes('_YY')) return this.$t('yearly')
      if (code.includes('_MM')) return this.$t('monthly')
      if (code.includes('_YM')) return this.$t('yearly')
      return ''
    },
    async sendAuthLink () {
      this.sendingAuthLink = true
      this.authExpired = false
      try {
        const resp = await this.$axios.post('/users/request_auth_link/', {
          email: this.email
        })
        this.authLinkSent = true
        this.authWaiting = true
        this.authLinkRecheckCount = 0

        // Poll check_auth_link every 5 seconds
        this.manualCheckInterval = setInterval(() => {
          this.authLinkRecheckCount++
          if (this.authLinkRecheckCount >= 60) {
            clearInterval(this.manualCheckInterval)
            this.authWaiting = false
            this.authExpired = true
            return
          }
          this.$axios.get('/users/check_auth_link/', { params: { token: resp.data.token } })
            .then(r => {
              clearInterval(this.manualCheckInterval)
              this.authWaiting = false
              this.completeAuth(r.data)
            })
            .catch(() => {})
        }, 5000)
      } catch {
        this.showMessage(['error', this.$t('auth_link_error')])
      } finally {
        this.sendingAuthLink = false
      }
    },
    completeAuth (userData) {
      this.authenticated = true
      this.authToken = userData.token

      // Set auth header for subsequent requests
      this.$axios.defaults.headers.common['Authorization'] = `Token ${userData.token}`

      // Set company slug header
      if (this.slug) {
        window.axios.defaults.headers.common['X-Company-Slug'] = this.slug
        this.$axios.defaults.headers.common['X-Company-Slug'] = this.slug
      }

      this.loadSubscriptionData()
    },
    async loadSubscriptionData () {
      this.loading = true
      try {
        // Fetch company
        const companyResp = await this.$axios.get('/companies/', {
          params: { slug: this.slug }
        })
        const companies = this.extractList(companyResp.data)
        if (companies.length > 0) {
          this.companyData = companies[0]
        }

        if (!this.companyData) {
          this.loading = false
          return
        }

        // Fetch subscriptions and bills in parallel
        const [subsResp] = await Promise.all([
          this.$axios.get('/subscription/subscriptions/', {
            params: {
              company: this.companyData.id,
              terminated: false,
              ordering: 'start_date',
              expand: 'contract',
              page_size: 1000
            }
          }),
          this.checkPendingBills()
        ])
        this.subscriptions = this.extractList(subsResp.data)
      } catch {
        this.showMessage(['error', this.$t('load_error')])
      } finally {
        this.loading = false
      }
    },
    async checkPendingBills () {
      this.loadingBills = true
      try {
        const resp = await this.$axios.get('/subscription/bills/', {
          params: {
            company: this.companyData.id,
            ordering: '-created',
            paid: false,
            page_size: 1000
          }
        })
        this.unpaidBills = this.extractList(resp.data)
        this.totalDue = this.unpaidBills.reduce((sum, bill) => {
          return sum + (parseFloat(bill.total_amount) || 0)
        }, 0)
      } catch {
        // ignore
      } finally {
        this.loadingBills = false
      }
    },
    onCardChange (card) {
      this.selectedCard = card
    },
    async payAllBills () {
      if (this.unpaidBills.length === 0) return
      if (this.paymentMethod === 'fpx') {
        await this.payByFpx()
        return
      }
      if (!this.selectedCard) return
      this.isProcessing = true
      try {
        for (const bill of this.unpaidBills) {
          if (parseFloat(bill.total_amount) <= 0) continue
          await this.$axios.post(`/subscription/bills/${bill.id}/charge_card/`, {
            card: this.selectedCard.id
          })
        }
        this.showMessage(['success', this.$t('payment_successful')])
        this.checkPendingBills()
      } catch {
        this.showMessage(['error', this.$t('payment_failed')])
      } finally {
        this.isProcessing = false
      }
    },
    async payByFpx () {
      // FPX is redirect-based and per-bill; pay the single non-zero bill (others are 0)
      const payableBills = this.unpaidBills.filter(b => parseFloat(b.total_amount) > 0)
      if (payableBills.length === 0) return
      // FPX cannot combine multiple bills into one transaction — send the user to the
      // bills list to settle each one individually
      if (payableBills.length > 1) {
        location.href = '/admin/billing/clinic-bills?page=1#subscription'
        return
      }
      const bill = payableBills[0]
      this.isProcessing = true
      const data = {
        method: this.companyData.currency.currency_code === 'IDR' ? 'xendit' : 'laravel',
        bank_code: this.fpxBankCode
      }
      try {
        const resp = await this.$axios.post(`/subscription/bills/${bill.id}/request_manual_payment/`, data)
        // Keep the processing dialog up until the browser redirects to the bank
        setTimeout(() => {
          location.href = resp.data.bill_url
        }, 1000)
      } catch {
        this.showMessage(['error', this.$t('payment_failed')])
        this.isProcessing = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
</style>

<i18n>
{
  "en": {
    "subscription_payment": "Subscription Payment",
    "email_address": "Email Address",
    "send_login_link": "SEND LOGIN LINK",
    "login_link_desc": "We will send a login link to your email to verify your identity",
    "check_email_inbox": "Check your email inbox",
    "check_email_inbox_desc": "Click the login link we sent to verify your identity",
    "waiting_for_approval": "Waiting for your approval",
    "link_expired": "Login link expired.",
    "click_to_resend": "Click here to resend",
    "auth_link_error": "Unable to send login link. Please check your email address.",
    "load_error": "Unable to load subscription data",
    "subscription": "Subscription",
    "active_subscriptions": "Active Subscriptions",
    "no_active_subscription": "No active subscriptions",
    "trial": "Trial",
    "trial_ends": "Trial ends",
    "trial_ended_at": "Trial ended at",
    "next_billing_date": "Next billing date",
    "contract_ends": "Contract ends",
    "total_due": "Total Due",
    "payment_method": "Payment Method",
    "credit_debit_card": "Credit or debit card",
    "online_banking": "Online banking",
    "choose_bank": "Choose bank for payment",
    "pay_now": "PAY NOW",
    "select_card_to_pay": "Please add or select a card above to pay",
    "processing_payment": "Processing payment...",
    "do_not_refresh": "Please do not refresh or navigate away from this screen",
    "payment_successful": "Payment successful",
    "no_pending_bills": "No pending bills",
    "payment_failed": "Payment failed. Please try again.",
    "clinic": "Clinic",
    "appointment": "Appointment",
    "review": "Review",
    "monthly": "Monthly",
    "yearly": "Yearly"
  },
  "id": {
    "subscription_payment": "Pembayaran Langganan",
    "email_address": "Alamat Email",
    "send_login_link": "KIRIM LINK LOGIN",
    "login_link_desc": "Kami akan mengirim link login ke email Anda untuk memverifikasi identitas Anda",
    "check_email_inbox": "Periksa kotak masuk email Anda",
    "check_email_inbox_desc": "Klik link login yang kami kirim untuk memverifikasi identitas Anda",
    "waiting_for_approval": "Menunggu persetujuan Anda",
    "link_expired": "Link login kedaluwarsa.",
    "click_to_resend": "Klik di sini untuk mengirim ulang",
    "auth_link_error": "Tidak dapat mengirim link login. Periksa alamat email Anda.",
    "load_error": "Tidak dapat memuat data langganan",
    "subscription": "Langganan",
    "active_subscriptions": "Langganan Aktif",
    "no_active_subscription": "Tidak ada langganan aktif",
    "trial": "Uji Coba",
    "trial_ends": "Uji coba berakhir",
    "trial_ended_at": "Uji coba berakhir pada",
    "next_billing_date": "Tanggal tagihan berikutnya",
    "contract_ends": "Kontrak berakhir",
    "total_due": "Total Tertunggak",
    "payment_method": "Metode Pembayaran",
    "credit_debit_card": "Kartu kredit atau debit",
    "online_banking": "Perbankan online",
    "choose_bank": "Pilih bank untuk pembayaran",
    "pay_now": "BAYAR SEKARANG",
    "select_card_to_pay": "Silakan tambah atau pilih kartu di atas untuk membayar",
    "processing_payment": "Memproses pembayaran...",
    "do_not_refresh": "Jangan muat ulang atau keluar dari halaman ini",
    "payment_successful": "Pembayaran berhasil",
    "no_pending_bills": "Tidak ada tagihan tertunggak",
    "payment_failed": "Pembayaran gagal. Silakan coba lagi.",
    "clinic": "Klinik",
    "appointment": "Janji Temu",
    "review": "Ulasan",
    "monthly": "Bulanan",
    "yearly": "Tahunan"
  }
}
</i18n>
